/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ExternalLink, Pencil, Save, X } from 'lucide-react';
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '@/api';
import { DOCUMENT_SUBJECT_SCOPES, DOSSIER_OPERATIONS, type DossierDocumentType, type DossierDocumentTypeEdit, type DossierOperation } from '@/api/areas/dossierQueue';
import { Badge } from '@/components/Badge';
import { DataTable, Field, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/i18n';
import { useAccess } from '@/lib/access';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { hubUrl } from '@/nav/hubs';
import { FormActions } from '../compliance/ui';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'] as const;
const MB = 1024 * 1024;

/** A tag input: Enter or comma adds, × removes. */
function TagInput({ value, onChange, disabled, placeholder, suggestions, id }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean; placeholder?: string; suggestions?: readonly string[]; id: string }) {
  const [draft, setDraft] = useState('');
  const add = (raw: string) => {
    const v = raw.trim().replace(/^\./, '').toLowerCase();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
  };
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-xs" data-tag={v}>
          {v}
          {!disabled ? (
            <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={`× ${v}`} onClick={() => onChange(value.filter((x) => x !== v))}>
              <X className="size-3" />
            </button>
          ) : null}
        </span>
      ))}
      <input
        id={id}
        list={suggestions ? `${id}-list` : undefined}
        className="min-w-20 flex-1 bg-transparent text-sm outline-none"
        value={draft}
        disabled={disabled}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => draft && add(draft)}
      />
      {suggestions ? (
        <datalist id={`${id}-list`}>
          {suggestions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </datalist>
      ) : null}
    </div>
  );
}

/**
 * "Áp dụng cho đuôi": a multi-select of the zones of the TLD catalogue (`GET /registry/tlds`),
 * with the zones the type already names kept even when the catalogue does not list them.
 */
function ZonePicker({ id, value, onChange, disabled }: { id: string; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  const { t } = useTranslation('declarations');
  const tlds = useApi((signal) => api.listTlds({ limit: 500 }, { signal }), []);
  const [filter, setFilter] = useState('');
  const known = tlds.status === 'success' ? tlds.data.items.map((x) => x.zone.replace(/^\./, '').toLowerCase()) : [];
  const zones = [...new Set([...known, ...value])].sort((a, b) => a.split('.').length - b.split('.').length || a.localeCompare(b));
  const q = filter.trim().replace(/^\./, '').toLowerCase();
  const shown = q ? zones.filter((z) => z.includes(q)) : zones;
  const toggle = (z: string, on: boolean) => onChange(on ? [...new Set([...value, z])].sort() : value.filter((x) => x !== z));
  return (
    <div className="grid gap-2 rounded-md border p-2" data-zone-picker={id}>
      <div className="flex items-center gap-2">
        <Input className="h-8" value={filter} placeholder={t('documentTypes.field.zoneFilter')} onChange={(e) => setFilter(e.target.value)} aria-label={t('documentTypes.field.zoneFilter')} />
        <span className="shrink-0 text-xs text-muted-foreground">{t('documentTypes.zonesPicked', { n: value.length })}</span>
      </div>
      {tlds.status === 'loading' ? <span className="text-xs text-muted-foreground">{t('documentTypes.zonesLoading')}</span> : null}
      {tlds.status === 'error' ? <DataError error={tlds.error} onRetry={tlds.reload} /> : null}
      <div className="grid max-h-48 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto sm:grid-cols-3">
        {shown.map((z) => (
          <label key={z} className="flex items-center gap-2 font-mono text-xs" data-zone={z}>
            <Checkbox checked={value.includes(z)} disabled={disabled} onCheckedChange={(c) => toggle(z, c === true)} />.{z}
          </label>
        ))}
      </div>
    </div>
  );
}

function toEdit(d: DossierDocumentType): DossierDocumentTypeEdit {
  return {
    version: d.version,
    reason: '',
    name_vi: d.name_vi,
    name_en: d.name_en,
    downloadable_template: d.downloadable_template,
    required_operations: d.required_operations,
    filled_by_ekyc: d.filled_by_ekyc,
    customer_download_only: d.customer_download_only,
    staff_only: d.staff_only,
    allowed_extensions: d.allowed_extensions,
    max_bytes: d.max_bytes,
    template_codes: d.template_codes,
    tld_zones: d.tld_zones,
    subject_scope: d.subject_scope ?? 'ALL',
    hstm_file_type: d.hstm_file_type,
    hstm_document_type: d.hstm_document_type,
    verification_methods: d.verification_methods,
    active: d.active,
  };
}

/** "Sửa loại hồ sơ" (the HostBill "Edit document" form). */
function EditForm({ d, canEdit, onSaved }: { d: DossierDocumentType; canEdit: boolean; onSaved: () => void }) {
  const { t } = useTranslation('declarations');
  const { t: td } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const trd = td as unknown as Lookup;
  const [e, setE] = useState<DossierDocumentTypeEdit>(() => toEdit(d));
  const write = useWrite((body: DossierDocumentTypeEdit, opts) => api.updateDossierDocumentType(d.id, body, opts));
  const set = <K extends keyof DossierDocumentTypeEdit>(k: K, v: DossierDocumentTypeEdit[K]) => setE((x) => ({ ...x, [k]: v }));
  const ro = !canEdit || write.busy;
  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (await write.run({ ...e, reason: e.reason.trim() })) onSaved();
  };
  const toggle = <T,>(list: T[], v: T, on: boolean) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));
  const flags: { key: 'downloadable_template' | 'filled_by_ekyc' | 'customer_download_only' | 'staff_only'; label: string }[] = [
    { key: 'downloadable_template', label: t('documentTypes.flag.download') },
    { key: 'filled_by_ekyc', label: t('documentTypes.flag.ekyc') },
    { key: 'customer_download_only', label: t('documentTypes.flag.onlyDownload') },
    { key: 'staff_only', label: t('documentTypes.flag.onlyAdmin') },
  ];
  return (
    <form onSubmit={submit} className="grid gap-4" data-document-type-form={d.id}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('documentTypes.field.nameVi')}>
          <Input value={e.name_vi} onChange={(x) => set('name_vi', x.target.value)} disabled={ro} maxLength={200} />
        </Field>
        <Field label={t('documentTypes.field.nameEn')}>
          <Input value={e.name_en} onChange={(x) => set('name_en', x.target.value)} disabled={ro} maxLength={200} />
        </Field>
      </div>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">{t('documentTypes.field.flags')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {flags.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm">
              <Checkbox checked={e[f.key]} disabled={ro} onCheckedChange={(c) => set(f.key, c === true)} />
              {f.label}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={e.active} disabled={ro} onCheckedChange={(c) => set('active', c === true)} />
            {t('documentTypes.flag.active')}
          </label>
        </div>
      </fieldset>
      <fieldset className="grid gap-2 rounded-md border p-3" data-required-scope={d.id}>
        <legend className="px-1 text-xs font-medium text-muted-foreground">{t('documentTypes.field.required')}</legend>
        <label className="flex items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={e.required_operations.length > 0}
            disabled={ro}
            data-field="required"
            onCheckedChange={(c) => set('required_operations', c === true ? (d.required_operations.length ? d.required_operations : ['REGISTER']) : [])}
          />
          {t('documentTypes.field.requiredToggle')}
        </label>
        {e.required_operations.length > 0 ? (
          <div className="grid gap-1.5 pl-6">
            <span className="text-xs text-muted-foreground">{t('documentTypes.field.requiredOps')}</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DOSSIER_OPERATIONS.map((o: DossierOperation) => (
                <label key={o} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={e.required_operations.includes(o)} disabled={ro} onCheckedChange={(c) => set('required_operations', DOSSIER_OPERATIONS.filter((x) => (x === o ? c === true : e.required_operations.includes(x))))} />
                  {tr(`documentTypes.operation.${o}`)}
                </label>
              ))}
            </div>
          </div>
        ) : null}
        <span className="text-xs text-muted-foreground">{t('documentTypes.field.requiredHint')}</span>
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t('documentTypes.field.tlds')}</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={`tld-${d.id}`} checked={e.tld_zones === null} disabled={ro} onChange={() => set('tld_zones', null)} data-field="all-tlds" />
              {t('documentTypes.allTlds')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name={`tld-${d.id}`} checked={e.tld_zones !== null} disabled={ro} onChange={() => set('tld_zones', e.tld_zones ?? [])} data-field="some-tlds" />
              {t('documentTypes.someTlds')}
            </label>
          </div>
          {e.tld_zones !== null ? <ZonePicker id={d.id} value={e.tld_zones} onChange={(v) => set('tld_zones', v)} disabled={ro} /> : null}
        </div>
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t('documentTypes.field.subjects')}</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {DOCUMENT_SUBJECT_SCOPES.map((sc) => (
              <label key={sc} className="flex items-center gap-2 text-sm">
                <input type="radio" name={`subject-${d.id}`} checked={e.subject_scope === sc} disabled={ro} onChange={() => set('subject_scope', sc)} data-subject-scope={sc} />
                {tr(`documentTypes.subject.${sc}`)}
              </label>
            ))}
          </div>
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('documentTypes.field.hstmFile')}>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={e.hstm_file_type ?? ''} disabled={ro} onChange={(x) => set('hstm_file_type', x.target.value ? Number(x.target.value) : null)} data-field="hstm-file">
            <option value="">{t('documentTypes.none')}</option>
            {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{`#${n} ${trd(`dossier.fileTypes.${n}`)}`}</option>
            ))}
          </select>
        </Field>
        <Field label={t('documentTypes.field.hstmDocument')}>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={e.hstm_document_type ?? ''} disabled={ro} onChange={(x) => set('hstm_document_type', x.target.value ? Number(x.target.value) : null)}>
            <option value="">{t('documentTypes.none')}</option>
            {DOSSIER_OPERATIONS.map((o, i) => (
              <option key={o} value={i + 1}>{`#${i + 1} ${tr(`documentTypes.operation.${o}`)}`}</option>
            ))}
          </select>
        </Field>
      </div>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">{t('documentTypes.field.methods')}</legend>
        <div className="grid gap-1.5">
          {[1, 2, 3, 4, 5].map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <Checkbox checked={e.verification_methods.includes(m)} disabled={ro} onCheckedChange={(c) => set('verification_methods', toggle(e.verification_methods, m, c === true).sort())} />
              {`#${m} ${trd(`dossier.certifyMethods.${m}`)}`}
            </label>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{t('documentTypes.field.methodsHint')}</span>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('documentTypes.field.extensions')} hint={t('documentTypes.field.extensionsHint')}>
          <TagInput id={`ext-${d.id}`} value={e.allowed_extensions} onChange={(v) => set('allowed_extensions', v)} disabled={ro} suggestions={EXTENSIONS} placeholder="pdf" />
        </Field>
        <Field label={t('documentTypes.field.maxSize')}>
          <Input type="number" min={1} max={20} step={1} value={Math.round(e.max_bytes / MB)} disabled={ro} onChange={(x) => set('max_bytes', Math.max(1, Math.min(20, Number(x.target.value) || 1)) * MB)} />
        </Field>
      </div>
      <Field label={t('documentTypes.field.template')}>
        <div className="flex flex-wrap items-center gap-2">
          {e.template_codes.length ? (
            e.template_codes.map((c) => (
              <code key={c} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {c}
              </code>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">{t('documentTypes.noTemplate')}</span>
          )}
          {e.template_codes.length ? (
            <Button asChild variant="outline" size="sm">
              <Link to={hubUrl('settings', 'domains', 'declarations')}>
                <ExternalLink /> {t('documentTypes.editTemplate')}
              </Link>
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" disabled title={t('documentTypes.noTemplate')}>
              <ExternalLink /> {t('documentTypes.editTemplate')}
            </Button>
          )}
        </div>
      </Field>
      <p className="text-xs text-muted-foreground">
        {d.registrar_signature === 'AUTO' ? t('documentTypes.signAuto') : t('documentTypes.signOptional')} · {t('documentTypes.updated', { at: formatDateTime(d.updated_at), by: d.updated_by ?? '—' })}
      </p>
      {canEdit ? (
        <>
          <Field label={t('read.reason')}>
            <Textarea rows={2} value={e.reason} onChange={(x) => set('reason', x.target.value)} disabled={write.busy} />
          </Field>
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
          <FormActions>
            <Button type="submit" size="sm" disabled={write.busy || !e.reason.trim() || !e.allowed_extensions.length || (e.tld_zones !== null && !e.tld_zones.length)}>
              <Save /> {t('documentTypes.save')}
            </Button>
          </FormActions>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{t('documentTypes.readOnly')}</p>
      )}
    </form>
  );
}

/**
 * Cài đặt › Tên miền & TLD › Loại hồ sơ: the dossier document types (the HostBill list the
 * owner's staff know, by number) — who uploads each one, its file extensions, the TLDs it applies
 * to, its VNNIC file and dossier type. The upload of the domain page, the required files and the
 * files sent to VNNIC follow this list.
 */
export function DocumentTypesPage() {
  const { t } = useTranslation('declarations');
  const tr = t as unknown as Lookup;
  const lang = useLanguage();
  useDocumentTitle(t('documentTypes.title'));
  const access = useAccess();
  const result = useApi((signal) => api.listDossierDocumentTypes({ signal }), []);
  const [open, setOpen] = useState<DossierDocumentType | null>(null);
  const name = (d: DossierDocumentType) => (lang === 'vi' ? d.name_vi : d.name_en);
  const columns: Column<DossierDocumentType>[] = [
    { key: 'code', header: '#', className: 'w-12', cell: (d) => <span className="font-mono text-xs">{d.code}</span> },
    {
      key: 'name',
      header: t('documentTypes.col.name'),
      className: 'whitespace-normal',
      cell: (d) => (
        <span className="grid gap-0.5">
          <span className={d.active ? 'font-medium' : 'font-medium text-muted-foreground line-through'}>{name(d)}</span>
          {d.hstm_file_type ? <span className="text-xs text-muted-foreground">{t('documentTypes.hstmShort', { n: d.hstm_file_type })}</span> : null}
        </span>
      ),
    },
    {
      key: 'flags',
      header: t('documentTypes.col.type'),
      className: 'whitespace-normal',
      cell: (d) => (
        <span className="flex flex-wrap gap-1" data-flags={d.id}>
          {d.downloadable_template ? <Badge tone="info" icon={false}>{t('documentTypes.badge.download')}</Badge> : null}
          {d.required_operations.length ? (
            <Badge tone="warn" icon={false} title={t('documentTypes.badge.requiredFor', { ops: d.required_operations.map((o) => tr(`documentTypes.operation.${o}`)).join(', ') })}>
              {t('documentTypes.badge.required')}
            </Badge>
          ) : null}
          {d.subject_scope === 'INDIVIDUAL' ? <Badge tone="neutral" icon={false}>{t('documentTypes.badge.individual')}</Badge> : null}
          {d.subject_scope === 'ORGANIZATION' ? <Badge tone="neutral" icon={false}>{t('documentTypes.badge.organization')}</Badge> : null}
          {d.filled_by_ekyc ? <Badge tone="ok" icon={false}>{t('documentTypes.badge.ekyc')}</Badge> : null}
          {d.customer_download_only ? <Badge tone="neutral" icon={false}>{t('documentTypes.badge.onlyDownload')}</Badge> : null}
          {d.staff_only ? <Badge tone="serious" icon={false}>{t('documentTypes.badge.onlyAdmin')}</Badge> : null}
          {d.registrar_signature === 'AUTO' ? <Badge tone="pending" icon={false}>{t('documentTypes.badge.autoSign')}</Badge> : null}
        </span>
      ),
    },
    { key: 'ext', header: t('documentTypes.col.extensions'), cell: (d) => <span className="font-mono text-xs">{d.allowed_extensions.join(' ')}</span> },
    {
      key: 'tlds',
      header: t('documentTypes.col.tlds'),
      className: 'max-w-64 whitespace-normal',
      cell: (d) =>
        d.tld_zones === null ? (
          <span className="text-sm">{t('documentTypes.allTldsShort')}</span>
        ) : (
          <span className="flex flex-wrap gap-1" data-zones={d.id}>
            {d.tld_zones.map((z) => (
              <Badge key={z} tone="info" icon={false}>
                .{z}
              </Badge>
            ))}
          </span>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('documentTypes.col.actions')}</span>,
      cell: (d) => (
        <Button size="sm" variant="ghost" onClick={() => setOpen(d)} data-edit-type={d.id}>
          <Pencil /> {access.has(['compliance.override']) ? t('documentTypes.edit') : t('documentTypes.view')}
        </Button>
      ),
    },
  ];
  return (
    <div className="grid gap-4">
      <PageHeader title={t('documentTypes.title')} subtitle={t('documentTypes.description')} />
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={6} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} />
          </div>
        ) : null}
        {result.status === 'success' ? <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.id} empty={<EmptyState title={t('documentTypes.empty')} />} /> : null}
      </Section>
      <Sheet open={open !== null} onOpenChange={(o) => (o ? undefined : setOpen(null))}>
        <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-xl" data-document-type-sheet>
          <SheetHeader>
            <SheetTitle>{open ? `#${open.code} ${name(open)}` : ''}</SheetTitle>
            <SheetDescription>{t('documentTypes.editHint')}</SheetDescription>
          </SheetHeader>
          {open ? (
            <div className="p-4">
              <EditForm
                key={`${open.id}@${open.version}`}
                d={open}
                canEdit={access.has(['compliance.override'])}
                onSaved={() => {
                  setOpen(null);
                  result.reload();
                }}
              />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
